import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Card, CardHeader, CardBody, Row, Col, FormGroup, Label, Input, Button, Table } from "reactstrap";
import axios from "../../config/axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const MetaCampaigns = () => {
  const history = useHistory();
  const { token: authToken, userLanguage } = useSelector((state) => state.auth);
  const t = {
    title: { en: "Meta Campaigns", fr: "Campagnes Meta", es: "Campanas Meta", zh: "Meta广告活动", pt: "Campanhas Meta", ar: "حملات ميتا" },
    refresh: { en: "Refresh", fr: "Actualiser", es: "Actualizar", zh: "刷新", pt: "Atualizar", ar: "تحديث" },
    refreshing: { en: "Refreshing...", fr: "Actualisation...", es: "Actualizando...", zh: "刷新中...", pt: "Atualizando...", ar: "جارٍ التحديث..." },
    adAccount: { en: "Ad account", fr: "Compte publicitaire", es: "Cuenta publicitaria", zh: "广告账户", pt: "Conta de anuncios", ar: "حساب الإعلانات" },
    name: { en: "Name", fr: "Nom", es: "Nombre", zh: "名称", pt: "Nome", ar: "الاسم" },
    objective: { en: "Objective", fr: "Objectif", es: "Objetivo", zh: "目标", pt: "Objetivo", ar: "الهدف" },
    status: { en: "Status", fr: "Statut", es: "Estado", zh: "状态", pt: "Status", ar: "الحالة" },
    created: { en: "Created", fr: "Cree", es: "Creado", zh: "创建时间", pt: "Criado", ar: "تاريخ الإنشاء" },
    id: { en: "ID", fr: "ID", es: "ID", zh: "ID", pt: "ID", ar: "المعرف" },
    action: { en: "Action", fr: "Action", es: "Accion", zh: "操作", pt: "Acao", ar: "الإجراء" },
    view: { en: "View", fr: "Voir", es: "Ver", zh: "查看", pt: "Ver", ar: "عرض" },
    none: { en: "No campaigns found.", fr: "Aucune campagne trouvee.", es: "No se encontraron campanas.", zh: "未找到广告活动。", pt: "Nenhuma campanha encontrada.", ar: "لم يتم العثور على حملات." },
    fail: { en: "Failed to load campaigns", fr: "Echec du chargement des campagnes", es: "Error al cargar campanas", zh: "加载广告活动失败", pt: "Falha ao carregar campanhas", ar: "فشل تحميل الحملات" },
  };
  const [adAccounts, setAdAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${authToken}` });

  useEffect(() => {
    if (!authToken) return;
    axios
      .get("/ad-accounts", { headers: headers() })
      .then((res) => {
        const list = res.data.data || [];
        setAdAccounts(list);
        if (list.length > 0) setSelectedAccountId(list[0].id);
      })
      .catch(() => setAdAccounts([]));
  }, [authToken]);

  const loadCampaigns = () => {
    if (!selectedAccountId) return;
    setLoading(true);
    axios
      .get(`/ad-accounts/${selectedAccountId}/campaigns`, { headers: headers() })
      .then((res) => setCampaigns(res.data.data || []))
      .catch((e) => toast.error(e.response?.data?.message || t.fail[userLanguage] || t.fail.en))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!selectedAccountId) return;
    loadCampaigns();
  }, [selectedAccountId]);

  return (
    <div className="animated fadeIn">
      <ToastContainer autoClose={5000} />
      <Row>
        <Col>
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <b>{t.title[userLanguage] || t.title.en}</b>
              <div>
                <Button color="secondary" size="sm" onClick={loadCampaigns} disabled={loading || !selectedAccountId}>
                  {loading ? t.refreshing[userLanguage] || t.refreshing.en : t.refresh[userLanguage] || t.refresh.en}
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              <FormGroup>
                <Label>{t.adAccount[userLanguage] || t.adAccount.en}</Label>
                <Input
                  type="select"
                  value={selectedAccountId}
                  onChange={(e) => setSelectedAccountId(e.target.value)}
                >
                  {adAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.accountName || a.accountId}
                    </option>
                  ))}
                </Input>
              </FormGroup>

              <Table bordered striped responsive size="sm">
                <thead>
                  <tr>
                    <th>{t.name[userLanguage] || t.name.en}</th>
                    <th>{t.objective[userLanguage] || t.objective.en}</th>
                    <th>{t.status[userLanguage] || t.status.en}</th>
                    <th>{t.created[userLanguage] || t.created.en}</th>
                    <th>{t.id[userLanguage] || t.id.en}</th>
                    <th>{t.action[userLanguage] || t.action.en}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="py-4 text-center">
                        <i className="fas fa-spinner fa-pulse fa-2x" aria-hidden />
                      </td>
                    </tr>
                  ) : campaigns.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-4 text-center text-muted">
                        {t.none[userLanguage] || t.none.en}
                      </td>
                    </tr>
                  ) : (
                    campaigns.map((c) => (
                      <tr key={c.id}>
                        <td>{c.name}</td>
                        <td>{c.objective}</td>
                        <td>{c.status}</td>
                        <td>{c.created_time ? new Date(c.created_time).toLocaleString() : "-"}</td>
                        <td style={{ fontFamily: "monospace" }}>{c.id}</td>
                        <td>
                          <Button
                            size="sm"
                            color="info"
                            onClick={() =>
                              history.push(`/seller/social-advertisement/campaign/${selectedAccountId}/${c.id}`)
                            }
                          >
                            {t.view[userLanguage] || t.view.en}
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default MetaCampaigns;
